import { Injectable } from '@angular/core';
import { Resolve } from '@angular/router';
import { EhrPatientService } from "../patient/ehr-patient.service";
import { ConfirmationDialogComponent } from "../confirm.dialog/confirmation.dialog.component";
import { MatDialog } from "@angular/material/dialog";
import { UtilityService } from "../shared/service/utility.service";
import { MedicationsService } from "./medications.service";
import { isIterable } from "rxjs/internal/util/isIterable";
import { AngularAppLogger } from '../angular-app-logger';

@Injectable({
  providedIn: 'root'
})
export class MedicationsResolver implements Resolve<any> {

  medicationRequest: any;
  startDate: Date;
  endDate: Date;

  constructor(
    readonly ehrpatientService: EhrPatientService,
    readonly dialog: MatDialog,
    readonly utility: UtilityService,
    readonly medicationsService: MedicationsService,
    readonly appLogger: AngularAppLogger
  ) {}

  resolve(): Promise<any> {
    const data = this.utility.data;
    this.medicationRequest = this.utility.bundleResources(data.medicationrequest).entry;
    return this.constructEhrDetails(this.medicationRequest)
      .then(payload => {
        this.medicationsService.updateMedications(payload);
        return {payload};
      });
  }

  /**
   * Retrieve and set MedicationRequest and Medication resources from EHR
   */
  // readonly processedMedications = new Set();
  async constructEhrDetails(medicationRequest) {
    let payload = [];
    let dialogShown = false;
    let userCanceled = false;

    // TODO: Race condition encountered in dialogShown - reverting to for loop for now
    // const medPromises = medicationRequest.map(async (medication) => {
    for (const medication of medicationRequest) {
      const entry: any = {
        authoredOn: medication.resource?.authoredOn,
        status: medication.resource?.status,
        resource: medication.resource,
        medRequestId: medication.resource?.id,
      };

      // medicationCodeableConcept
      if (medication.resource?.medicationCodeableConcept) {
        entry.endDate = this.getEndDate(medication.resource);
        entry.medicationDisplay = medication.resource?.medicationCodeableConcept?.text;
        entry.startDate = this.getStartDate(medication.resource);
      }

      // medicationReference
      else if (medication.resource?.medicationReference) {
        // EPIC Specific
        const medReferenceRequest = medication?.resource?.medicationReference.reference;

        const medicationresponse = await this.ehrpatientService
          .getMedicationRef(medReferenceRequest)
          .toPromise();

        const leafMeds = new Map();
        const nonLeafMeds = new Map();
        const shouldSkip = await this.getInternalMeds(leafMeds, nonLeafMeds, medicationresponse.ingredient);

        // shouldSkip will be true if Substance is found in any internal level of the medication resources.
        if (shouldSkip) {
          if (!dialogShown) {
            const userAcknowledged = await this.showConfirmationDialog(true);
            dialogShown = true;
            if (!userAcknowledged) {
              userCanceled = true;
              return;
            }
          }
          // Skip the current medication and continue to the next one
          continue;
        }

        // Split the meds into leaf and nonleaf
        entry.innerlevelMedications = {
          leaf: leafMeds,
          nonLeaf: nonLeafMeds,
        }
        entry.medicationresponse = medicationresponse;

        entry.medicationDisplay = medication.resource?.medicationReference?.display;
        entry.startDate = this.getStartDate(medication.resource);
        entry.endDate = this.getEndDate(medication.resource);
      }
      payload.push(entry);
    }

    return userCanceled ? null :  payload;
  }

  /**
   * Custom confirmation dialog to show user
   * that Substance resource was encountered
   * @param showCheckbox
   */
  async showConfirmationDialog(showCheckbox: boolean): Promise<boolean> {
    return new Promise((resolve) => {
      const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
        data: {
          confirmMessage: 'The patient has medications that contain the FHIR resource Substance. The Reporting App does not currently support this resource. Medications with Substance will not be processed. Please review the forms in FN3 to verify the medication data.',
          showCheckbox: showCheckbox
        },
        width: '1000px',
        panelClass: "custom-dialog-container",
      });

      dialogRef.afterClosed().subscribe((confirmed: boolean) => {
        resolve(confirmed); // Return the user's response: true (Confirm) or false (Cancel)
      });
    });
  }

  /**
   * Recursively retrieve the inner-level medication resources
   * (go as deep as data is available) with this recursion
   * if Substance is found, this will return true and stop recursion
   * @param leafMeds
   * @param nonLeafMeds
   * @param ingredient
   */
  async getInternalMeds(leafMeds, nonLeafMeds, ingredient) {
    if (isIterable(ingredient)) {
      for (const ing of ingredient) {
        if (await this.getInternalMeds(leafMeds, nonLeafMeds, ing))
          return true;
      }
    } else if (ingredient?.itemReference?.reference) {
      const reference = ingredient?.itemReference?.reference;
      if (reference.includes('Substance/')) {
        this.appLogger.logInfo(`Found Substance resource reference at ${reference}`, { context: 'Attempting to fetch all internal meds' });
        return true;
      }
      const med = await this.ehrpatientService
        .getMedicationRef(reference)
        .toPromise();

      const hasIngredients = Array.isArray(med.ingredient) && med.ingredient.length > 0;
      const isCodeableConceptOnly = hasIngredients && med.ingredient.every(ing =>  ing.itemCodeableConcept && !ing.itemReference);
      const hasCode = !!med.code;

      // Is Leaf or nonLeaf med
      const isLeaf = !hasIngredients || isCodeableConceptOnly || (!hasIngredients && hasCode);
      if (isLeaf) {
        leafMeds.set(med.id, med);
      } else {
        nonLeafMeds.set(med.id, med);
        const foundSubstance = await this.getInternalMeds(leafMeds, nonLeafMeds, med.ingredient);
        if (foundSubstance) {
          // substance found in an internal level of nonLeaf Med
          return true;
        }
      }
    }
    return false; // No Substance found, keep processing
  }

  /**
   * TODO: Needs some rework as part of LogicaHealth removal story
   * @param resource
   */
  //--Logica---
  getStartDate(resource: any) {
    if (resource && resource.dosageInstruction) {
      this.startDate = resource.dosageInstruction[0]?.timing?.repeat?.boundsPeriod?.start;
      return this.startDate;
    }
    return "N/A";
  }

  getEndDate(resource: any) {
    if (resource) {
      if (resource.dosageInstruction) {
        this.endDate = resource.dosageInstruction[0]?.timing?.repeat?.boundsPeriod?.end;
        return this.endDate;
      }
      if (resource.dispenseRequest) {
        const supplyDurationValue = resource.dispenseRequest?.expectedSupplyDuration?.value;
        const durationUnit = resource.dispenseRequest?.expectedSupplyDuration?.unit;
        return `${supplyDurationValue} ${durationUnit}`;
      }
    }
    return "N/A";
  }
}
